import { ValidationResult, useSprintMutation, useSprintQueryClient, SprintMutationResult } from "sprint-common-web";

import { UpdateCloudSoundtrackAssetLanguageRequest } from "../../../../../../../../common/api/master-api/assets/details/UpdateCloudSoundtrackAssetLanguageRequest";
import { UpdateCloudSoundtrackAssetLanguageResponse } from "../../../../../../../../common/api/master-api/assets/details/UpdateCloudSoundtrackAssetLanguageResponse";
import { ValidateCloudSoundtrackAssetLanguageRequest } from "../../../../../../../../common/api/master-api/assets/details/ValidateCloudSoundtrackAssetLanguageRequest";
import { useAssetsApi } from "../../../../../../../../common/api/master-api/assets/useAssetsApi";
import { CloudAssetQueryKey } from "../../../../../common/CloudAssetQueryKey";

export interface UseEditCloudAsset {
    validateCloudSoundtrackAssetLanguage: SprintMutationResult<
        ValidateCloudSoundtrackAssetLanguageRequest,
        ValidationResult
    >;
    updateCloudSoundtrackAssetLanguage: SprintMutationResult<
        UpdateCloudSoundtrackAssetLanguageRequest,
        UpdateCloudSoundtrackAssetLanguageResponse
    >;
}

export const useEditCloudSoundtrackAssetLanguage = ({
    invalidationKey
}: {
    invalidationKey?: string;
}): UseEditCloudAsset => {
    const assetsApi = useAssetsApi();
    const sprintQueryClient = useSprintQueryClient();

    const validateCloudSoundtrackAssetLanguage = useSprintMutation<
        ValidateCloudSoundtrackAssetLanguageRequest,
        ValidationResult
    >({
        mutationFn: async ({ assetId, soundtrackLanguageId }): Promise<ValidationResult> => {
            if (!assetId) {
                throw new Error("Asset ID is required to validate soundtrack language.");
            }

            return assetsApi.validateCloudSoundtrackAssetLanguage({ assetId, soundtrackLanguageId });
        }
    });

    const updateCloudSoundtrackAssetLanguage = useSprintMutation<
        UpdateCloudSoundtrackAssetLanguageRequest,
        UpdateCloudSoundtrackAssetLanguageResponse
    >({
        mutationFn: async ({ assetId, soundtrackLanguageId }: UpdateCloudSoundtrackAssetLanguageRequest) => {
            if (!assetId) {
                throw new Error("Asset ID is required to update soundtrack language.");
            }

            return assetsApi.updateCloudSoundtrackAssetLanguage({ assetId, soundtrackLanguageId });
        },
        onSettled: async () => {
            await sprintQueryClient.invalidateQueries({
                queryKey: [CloudAssetQueryKey.CloudAssetDetails, invalidationKey]
            });
        }
    });

    return {
        validateCloudSoundtrackAssetLanguage,
        updateCloudSoundtrackAssetLanguage
    };
};
